// src/components/captures/CaptureModal.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TimeBox } from '../../types/timeBox';
import { ArrowLeft } from '../common/icons';
import ResponsiveModal from '../common/ResponsiveModal';
// TimeBoxContext 已移除，改用 props 傳遞

interface CaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (content: string, relatedToCurrentTimeBox: boolean) => void;
  initialContent?: string;
  userId: string;
  isSubmitting?: boolean;
  // 保留 currentTimeBox 作為可選屬性，向後兼容
  currentTimeBox?: TimeBox | null;
  // 新增的屬性
  isEditing?: boolean; // 是否為編輯模式
  title?: string; // 自定義標題
}

const CaptureModal: React.FC<CaptureModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialContent = '',
  userId,
  isSubmitting = false,
  currentTimeBox: propTimeBox,
  isEditing = false, // 預設為創建模式
  title // 如果未提供，將使用預設標題
}) => {
  // 使用 prop 傳入的時間箱（Context 已移除）
  const currentTimeBox = propTimeBox;
  
  const [content, setContent] = useState(initialContent);
  const [error, setError] = useState<string | null>(null);
  const [localSubmitting, setLocalSubmitting] = useState(false); // 本地提交狀態
  
  // 創建一個 ref 來引用 textarea 元素
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // 組合外部和本地的提交狀態
  const isActuallySubmitting = isSubmitting || localSubmitting;
  
  // 當模態框打開或 initialContent 變化時更新內容
  useEffect(() => {
    if (isOpen) {
      setContent(initialContent);
      setError(null);
      setLocalSubmitting(false);

      // 在下一個渲染週期調整文本域高度並設定游標位置
      setTimeout(() => {
        adjustTextareaHeight();

        // 將游標移到文字末端
        if (textareaRef.current && initialContent) {
          const length = initialContent.length;
          textareaRef.current.setSelectionRange(length, length);
          textareaRef.current.focus();
        }
      }, 0);
    }
  }, [isOpen, initialContent]);
  
  // 自動調整 textarea 高度的函數
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      // 重置高度以獲取實際需要的高度
      textarea.style.height = 'auto';
      // 設置新高度為 scrollHeight
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };
  
  // 處理內容變更，自動調整高度
  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
    // 在下一個渲染週期調整高度
    setTimeout(() => {
      adjustTextareaHeight();
    }, 0);
  };
  
  // 使用 useCallback 包裝 handleSubmit，避免不必要的重新創建
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('表單提交觸發', { content, isActuallySubmitting: isSubmitting || localSubmitting });
    
    if (!content.trim() || isActuallySubmitting) {
      console.log('內容為空或正在提交中，忽略提交');
      return;
    }
    
    setError(null);
    setLocalSubmitting(true);
    
    try {
      console.log('準備調用父組件 onSubmit');
      const relateToCurrentTimeBox = !!currentTimeBox;
      
      // 這裡不直接關閉模態框，讓父組件決定何時關閉
      await onSubmit(content, relateToCurrentTimeBox);
      
      console.log('父組件 onSubmit 完成');
      // 這裡不清空內容，讓父組件控制模態框的狀態
    } catch (err) {
      console.error('Error during submission:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to process: ${errorMessage}`);
    } finally {
      setLocalSubmitting(false);
    }
  }, [content, currentTimeBox, isSubmitting, onSubmit]);
  
  // 如果有當前時間箱，顯示關聯信息作為標題下方內容
  const headerContent = currentTimeBox ? (
    <div className="px-4 pt-2 text-xs text-gray-500 bg-gray-50 pb-1 border-b border-gray-100">
      Related to: {currentTimeBox.title}
    </div>
  ) : null;
  
  // 根據是否為編輯模式決定顯示的標題
  const modalTitle = title || (isEditing ? "Edit Capture" : "Quick Capture");
  
  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      leftAction={
        <button 
          className="text-gray-500 hover:text-gray-700 mr-2" 
          onClick={onClose}
          aria-label="Back"
          type="button"
        >
          <ArrowLeft size={20} />
        </button>
      }
      saveButton={{
        onSave: () => {
          const form = document.getElementById('capture-form') as HTMLFormElement;
          if (form) {
            form.requestSubmit();
          }
        },
        disabled: !content.trim(),
        isSubmitting: isActuallySubmitting,
        text: 'Save',
        submittingText: 'Saving...'
      }}
      headerContent={headerContent}
    >
      {/* 整個內容區會滾動，而不是 textarea 本身 */}
      <form id="capture-form" onSubmit={handleSubmit} className="w-full">
        {/* 錯誤訊息顯示區塊 */}
        {error && (
          <div className="px-4 py-3 bg-red-50 text-red-600 text-sm">
            {error}
          </div>
        )}
        
        {/* 內容區域 - textarea 會自動增長高度，不顯示滾動條 */}
        <div className="p-4">
          <textarea
            ref={textareaRef}
            value={content}
            onChange={handleContentChange}
            className="w-full p-0 border-none focus:outline-none focus:ring-0 resize-none break-words whitespace-pre-wrap text-lg"
            placeholder={isEditing ? "Edit your note..." : "Jot down brilliant ideas before they escape!"}
            rows={3}
            autoFocus
            disabled={isActuallySubmitting}
            style={{
              overflow: "hidden", // 隱藏滾動條
              minHeight: "80px"   // 最小高度
            }}
          />
        </div>
      </form>
    </ResponsiveModal>
  );
};

export default CaptureModal;